import {
  Box,
  Center,
  CloseButton,
  Flex,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import CloseOutlinedIcon from '@gamma/ui/icons/CloseOutlinedIcon';
import { Column, Panel, PanelHeader, PanelHeading } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { TimestampCell } from '@investigator/shared/components';
import { i18n } from '@investigator/shared/localization';
import { SlideDrawerContext } from '@investigator/shared/providers';
import moment from 'moment';
import { useContext } from 'react';
import { SanitizedSearchBasedAlert } from '../..';

const { alerts, detection } = i18n.pages.detections;

interface AlertRowSearchBasedProps {
  item: SanitizedSearchBasedAlert;
}

interface AlertRowSplitSearchBasedProps {
  item: SanitizedSearchBasedAlert;
  loading: boolean | undefined;
}

export const AlertRowSearchBased = ({ item }: AlertRowSearchBasedProps) => {
  return (
    <HStack spacing={4}>
      <VStack alignItems="start">
        <TimestampCell
          timestamp={item?.alert_timestamp?.observed ?? 0}
          isFromNow={false}
        />
        <HStack color="text.200">
          <Text textStyle="body-md">{item?.alert_info?.alert_name}</Text>
          <Text textStyle="body-md">|</Text>
          <Text textStyle="body-md">{item?.alert_entity?.entity_name}</Text>
        </HStack>
      </VStack>
    </HStack>
  );
};

export const AlertRowSplitSearchBased = ({
  item,
  loading,
}: AlertRowSplitSearchBasedProps) => {
  const time = moment.unix(item?.alert_timestamp?.observed ?? 0);
  const formattedTime = time.format();
  const { setIsSlideDrawerOpen, slideDrawerOnClose } =
    useContext(SlideDrawerContext);

  if (loading) {
    return (
      <Center w="100%">
        <GraphQLReqStatus loading={loading} />
      </Center>
    );
  }

  return (
    <span data-testid="search-based-alerts-wrapper">
      <VStack w="100%" alignItems="stretch" position="relative">
        <CloseButton
          onClick={() => {
            slideDrawerOnClose?.();
            setIsSlideDrawerOpen?.(false);
          }}
          size="md"
          aria-label="Close drawer"
          position="absolute"
          top={2}
          right={2}
          zIndex="docked"
        >
          <CloseOutlinedIcon />
        </CloseButton>
        <Box w="100%" pl={4} pr={8} py={3}>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            gap={3}
            wrap="wrap"
          >
            <Text
              textStyle="h5"
              wordBreak="break-word"
              overflowWrap="anywhere"
              flex="1 1 auto"
              minW={0}
            >
              {`${item?.alert_info?.alert_name} | ${formattedTime}`}
            </Text>
          </Flex>
        </Box>
      </VStack>
      <Column>
        <Panel data-testid="search-based-details-panel">
          <PanelHeader divider data-testid="search-based-alert-heading">
            <PanelHeading>{detection.details}</PanelHeading>
          </PanelHeader>
          <VStack alignItems="start">
            <VStack spacing={4} alignItems="start">
              <VStack spacing={1} alignItems="start">
                <HStack spacing={1} alignItems="start">
                  <Text textStyle="body-md" color="text.200">
                    {alerts.category}:
                  </Text>
                  <Text>{item?.alert_info?.alert_name}</Text>
                </HStack>
                <HStack spacing={1} alignItems="start">
                  <Text textStyle="body-md" color="text.200">
                    {alerts.timestamp}:
                  </Text>
                  <Text>{formattedTime}</Text>
                </HStack>
              </VStack>
            </VStack>
          </VStack>
        </Panel>
      </Column>
    </span>
  );
};
